import { useEffect, useState } from 'react'
import { Ban, Minus, Plus, QrCode, Search } from 'lucide-react'
import { foutTekst, vraag } from '../lib/api'
import { datum, datumTijd, getal } from '../lib/opmaak'
import { MAANDNAAM, normaliseerCode } from '../../supabase/functions/_gedeeld/regels.ts'
import { Lade } from '../onderdelen/Lade'
import { Scanner } from '../onderdelen/Scanner'
import type { Sessie } from './Beheer'

interface Gevonden { id: string; saldo: number; email: string; naam: string; telefoon: string; codes: string[]; laatsteScan: string | null }
interface Detail {
  id: string; saldo: number; email: string; naam: string; telefoon: string; oudeCredits: number | null; toestemming: string | null; toestemmingOp: string | null
  aangemaakt: string; bron: string; laatsteScan: string | null; chauffeurBereikt: string | null
  verjaardag: { dag: number; maand: number } | null; verjaardagJaar: number | null
  wasbeurten: number; rang: string | null
  vouchers: { id: string; naam: string; uitgegeven: string; verloopt: string | null; gebruikt: string | null }[]
  tags: { code: string; actief: boolean; aangemaakt: string }[]
  zelfdeEmail: { id: string; saldo: number; bron: string; aangemaakt: string; codes: string[] }[]
  transacties: { t: string; soort: string; punten: number; saldoNa: number; vestiging: string; medewerker: string; beloning: string; tag: string; reden: string }[]
}

const SOORT: Record<string, string> = {
  scan: 'Wasbeurt', inwisseling: 'Ingewisseld', activering: 'Tag geactiveerd', 'tag-toegevoegd': 'Tag toegevoegd', samengevoegd: 'Samengevoegd', import: 'Overgezet uit het oude systeem', correctie: 'Correctie',
}

export function Klanten(_: { s: Sessie }) {
  const [q, zetQ] = useState('')
  const [lijst, zetLijst] = useState<Gevonden[] | null>(null)
  const [fout, zetFout] = useState<string | null>(null)
  const [camera, zetCamera] = useState(false)
  const [open, zetOpen] = useState<Detail | null>(null)

  const zoek = async (term = q) => {
    zetFout(null)
    try { zetLijst((await vraag<{ klanten: Gevonden[] }>('klant-zoeken', { q: term.trim() })).klanten) } catch (e) { zetFout(foutTekst(e)) }
  }
  const openen = async (id: string) => {
    try { zetOpen((await vraag<{ klant: Detail }>('klant', { id })).klant) } catch (e) { zetFout(foutTekst(e)) }
  }
  /* Doorgeklikt vanuit een vestiging: /beheer/klanten?q=CODE. */
  useEffect(() => {
    const c = new URLSearchParams(location.search).get('q')
    if (!c) return
    zetQ(c)
    vraag<{ klanten: Gevonden[] }>('klant-zoeken', { q: c }).then((r) => {
      zetLijst(r.klanten)
      const enige = r.klanten.length === 1 ? r.klanten[0] : undefined
      if (enige) openen(enige.id)
    }).catch((e) => zetFout(foutTekst(e)))
  }, [])

  return (
    <>
      <div className="pagina-kop"><h2>Klanten</h2></div>
      <form className="filter-balk" onSubmit={(e) => { e.preventDefault(); zoek() }}>
        <input className="invoer" type="search" value={q} onChange={(e) => zetQ(e.target.value)} placeholder="Zoek een klant" enterKeyHint="search" autoCapitalize="none" />
        <button type="button" className="knop rond" onClick={() => zetCamera(true)} aria-label="Scan een tag"><QrCode size={20} /></button>
        <button className="knop geel rond" aria-label="Zoeken"><Search size={20} /></button>
      </form>
      {fout && <div className="melding fout">{fout}</div>}
      {lijst && (
        lijst.length ? (
          <div className="lijst">
            {lijst.map((k) => (
              <button key={k.id} className="locatie" onClick={() => openen(k.id)}>
                <h3 className="code-pil">{k.codes.join(' · ') || 'geen actieve tag'}</h3>
                <b className="cijfer">{getal(k.saldo)} pt</b>
                <span className="sub"><span>{k.naam || k.email || k.telefoon || 'geen gegevens'}</span><span>· laatste wasbeurt {datum(k.laatsteScan)}</span></span>
              </button>
            ))}
          </div>
        ) : <div className="leeg-staat">Geen klant gevonden.</div>
      )}
      {!lijst && <p className="stil">Zoek op de code van de kaart (of het begin ervan), het volledige e-mailadres, een telefoonnummer, een naam of het klantnummer uit een export.</p>}

      <Lade titel="Scan een tag" open={camera} sluit={() => zetCamera(false)} kinderen={camera && (
        <Scanner gevonden={(t) => { zetCamera(false); const c = normaliseerCode(t) ?? t; zetQ(c); zoek(c) }} />
      )} />
      <Lade titel={open ? `${open.tags.find((t) => t.actief)?.code ?? open.naam ?? 'Klant'} · ${getal(open.saldo)} punten` : ''} open={!!open} sluit={() => zetOpen(null)} breed
        kinderen={open && <KlantDetail k={open} zet={zetOpen} />} />
    </>
  )
}

function KlantDetail({ k, zet }: { k: Detail; zet: (d: Detail) => void }) {
  const [nieuw, zetNieuw] = useState('')
  const [fout, zetFout] = useState<string | null>(null)
  const [scannen, zetScannen] = useState(false)
  const doe = async (actie: string, lijf: Record<string, unknown>) => {
    zetFout(null)
    try { zet((await vraag<{ klant: Detail }>(actie, lijf)).klant); zetNieuw('') } catch (e) { zetFout(foutTekst(e)) }
  }
  return (
    <div className="lijst" style={{ gap: 16 }}>
      <p className="stil klein" style={{ margin: 0 }}>Klantnummer <span className="code-pil" style={{ userSelect: 'all' }}>{k.id}</span></p>
      <div className="tegels" style={{ marginBottom: 0 }}>
        <div className="kaart tegel"><small>Saldo</small><b>{getal(k.saldo)}</b>{k.oudeCredits !== null && <span>{getal(k.oudeCredits)} oude credits</span>}</div>
        <div className="kaart tegel"><small>Klant sinds</small><b style={{ fontSize: 18 }}>{datum(k.aangemaakt)}</b><span>via {k.bron}</span></div>
        <div className="kaart tegel"><small>E-mail</small><b style={{ fontSize: 15, wordBreak: 'break-all' }}>{k.email || 'geen e-mail'}</b>
          {k.naam && <span>{k.naam}</span>}{k.telefoon && <span> · {k.telefoon}</span>}<br />
          <span>{k.toestemming === 'SUBSCRIBED' ? `toestemming sinds ${datum(k.toestemmingOp)}` : k.toestemming === 'UNSUBSCRIBED' ? 'afgemeld' : 'geen toestemming'}</span></div>
        <div className="kaart tegel"><small>Chauffeur</small><b style={{ fontSize: 18 }}>{k.chauffeurBereikt ? datum(k.chauffeurBereikt) : 'nog niet behaald'}</b></div>
        <div className="kaart tegel"><small>Rang</small><b style={{ fontSize: 17 }}>{k.rang ?? '—'}</b><span>{getal(k.wasbeurten)} wasbeurten</span></div>
        <div className="kaart tegel"><small>Verjaardag</small><b style={{ fontSize: 18 }}>{k.verjaardag ? `${k.verjaardag.dag} ${MAANDNAAM[k.verjaardag.maand - 1]}` : 'niet ingevuld'}</b>
          {k.verjaardagJaar && <span>punten gehad in {k.verjaardagJaar}</span>}</div>
      </div>

      {k.zelfdeEmail.length > 0 && (
        <section className="kaart vol" style={{ borderColor: 'var(--line-brand)' }}>
          <b>Ook een account met dit e-mailadres</b>
          <p className="stil klein" style={{ margin: '4px 0 6px' }}>
            Waarschijnlijk registreerde de klant een nieuwe kaart terwijl er al een (oud) account was. Samenvoegen zet de punten, kaarten en geschiedenis bij deze klant.
          </p>
          {k.zelfdeEmail.map((z) => (
            <div key={z.id} className="beloning-rij">
              <span className="vul"><b className="cijfer">{getal(z.saldo)} punten</b> · {z.bron}, sinds {datum(z.aangemaakt)}<br />
                <span className="stil klein code-pil">{z.codes.join(' · ') || 'geen kaart'}</span></span>
              <button className="knop geel klein-knop" onClick={() => {
                if (confirm(`De ${getal(z.saldo)} punten en ${z.codes.length} kaart(en) van dat account bij deze klant zetten? Dat account verdwijnt daarna.`)) doe('klant-samenvoegen', { id: k.id, met: z.id })
              }}>Samenvoegen</button>
            </div>
          ))}
        </section>
      )}

      <PuntenCorrectie k={k} zet={zet} />

      <KlantVouchers k={k} zet={zet} />

      <section>
        <div className="sectie-kop"><h2>Tags</h2></div>
        {k.tags.map((t) => (
          <div key={t.code} className="beloning-rij">
            <span className="vul"><b className="code-pil">{t.code}</b><br /><span className="stil klein">sinds {datum(t.aangemaakt)}</span></span>
            {t.actief
              ? <button className="knop gevaar klein-knop" onClick={() => { if (confirm(`Tag ${t.code} intrekken? Hij werkt dan nergens meer. Het saldo blijft bij de klant.`)) doe('tag-intrekken', { code: t.code }) }}><Ban size={16} />Intrekken</button>
              : <span className="badge dicht">Ingetrokken</span>}
          </div>
        ))}
        <button type="button" className="knop breed" style={{ marginTop: 10 }} onClick={() => zetScannen(true)}><QrCode size={20} />Nieuwe kaart scannen</button>
        <form className="filter-balk" style={{ marginTop: 8 }} onSubmit={(e) => { e.preventDefault(); doe('tag-toevoegen', { id: k.id, code: normaliseerCode(nieuw) ?? nieuw }) }}>
          <input className="invoer" value={nieuw} onChange={(e) => zetNieuw(e.target.value)} placeholder="Of typ de code" autoCapitalize="characters" aria-label="Code van de nieuwe kaart" />
          <button className="knop" disabled={!normaliseerCode(nieuw)}><Plus size={18} />Koppelen</button>
        </form>
        {fout && <div className="melding fout">{fout}</div>}
      </section>
      <Lade titel="Scan de nieuwe kaart" open={scannen} sluit={() => zetScannen(false)} kinderen={scannen && (
        <Scanner gevonden={(t) => {
          zetScannen(false)
          const c = normaliseerCode(t)
          if (!c) { zetFout('Dat is geen puntenkaart. Scan de QR-code op de kaart.'); return }
          zetNieuw(c)
          doe('tag-toevoegen', { id: k.id, code: c })
        }} />
      )} />

      <section>
        <div className="sectie-kop"><h2>Geschiedenis</h2></div>
        <div className="tabel-schuif">
          <table className="tabel kaartjes">
            <thead><tr><th>Wanneer</th><th>Wat</th><th>Vestiging</th><th className="getal">Punten</th><th className="getal">Saldo</th></tr></thead>
            <tbody>
              {k.transacties.map((t, i) => (
                <tr key={i}>
                  <td className="hoofd">{datumTijd(t.t)}</td>
                  <td data-l="Wat">{SOORT[t.soort] ?? t.soort}{t.beloning ? ` · ${t.beloning}` : ''}{t.reden ? ` · ${t.reden}` : ''}{t.medewerker ? ` · ${t.medewerker}` : ''}</td>
                  <td data-l="Vestiging">{t.vestiging || '—'}</td>
                  <td className="getal" data-l="Punten">{t.punten > 0 ? `+${t.punten}` : t.punten || '—'}</td>
                  <td className="getal" data-l="Saldo">{t.saldoNa}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  )
}

/* Punten bijboeken of afhalen, met een reden. Komt in de geschiedenis met wie het deed. */
function PuntenCorrectie({ k, zet }: { k: Detail; zet: (d: Detail) => void }) {
  const [aantal, zetAantal] = useState('')
  const [reden, zetReden] = useState('')
  const [bezig, zetBezig] = useState(false)
  const [uit, zetUit] = useState<{ tekst: string; goed: boolean } | null>(null)
  const n = Number(aantal)
  const geldig = Number.isInteger(n) && n >= 1 && n <= 1000 && reden.trim().length >= 3
  const boek = async (teken: 1 | -1) => {
    const punten = teken * n
    if (!confirm(`${teken > 0 ? `${getal(n)} punten bijboeken bij` : `${getal(n)} punten afhalen van`} deze klant?\nReden: ${reden.trim()}`)) return
    zetBezig(true); zetUit(null)
    try {
      const r = await vraag<{ klant: Detail }>('punten-corrigeren', { id: k.id, punten, reden })
      zet(r.klant); zetAantal(''); zetReden('')
      zetUit({ tekst: `${teken > 0 ? 'Bijgeboekt' : 'Afgehaald'}: ${getal(n)} punten. Nieuw saldo ${getal(r.klant.saldo)}.`, goed: true })
    } catch (e) { zetUit({ tekst: foutTekst(e), goed: false }) } finally { zetBezig(false) }
  }
  return (
    <section>
      <div className="sectie-kop"><h2>Punten corrigeren</h2></div>
      <div className="punten-correctie">
        <label className="veld"><span>Aantal punten</span>
          <input value={aantal} inputMode="numeric" placeholder="bijv. 5" onChange={(e) => zetAantal(e.target.value.replace(/\D/g, '').slice(0, 4))} />
        </label>
        <label className="veld"><span>Reden</span>
          <input value={reden} maxLength={200} placeholder="bijv. scan mislukt op 12 okt" onChange={(e) => zetReden(e.target.value)} />
        </label>
      </div>
      <div className="knoppenrij" style={{ marginTop: 10 }}>
        <button className="knop gevaar" disabled={!geldig || bezig || n > k.saldo} onClick={() => boek(-1)}><Minus size={18} />Afhalen</button>
        <button className="knop geel" disabled={!geldig || bezig} onClick={() => boek(1)}><Plus size={18} />Bijboeken</button>
      </div>
      {geldig && n > k.saldo && <p className="stil klein" style={{ marginTop: 6 }}>Afhalen kan tot het saldo ({getal(k.saldo)}).</p>}
      {uit && <div className={`melding ${uit.goed ? 'ok' : 'fout'}`} style={{ marginTop: 8 }}>{uit.tekst}</div>}
    </section>
  )
}

/* Vouchers van deze klant: zien, eentje geven, of een ongebruikte intrekken. */
function KlantVouchers({ k, zet }: { k: Detail; zet: (d: Detail) => void }) {
  const [promoties, zetPromoties] = useState<{ id: string; naam: string; actief: boolean }[] | null>(null)
  const [kies, zetKies] = useState('')
  const [fout, zetFout] = useState<string | null>(null)
  const herlaad = async () => zet((await vraag<{ klant: Detail }>('klant', { id: k.id })).klant)
  const nu = Date.now()
  return (
    <section>
      <div className="sectie-kop"><h2>Vouchers</h2></div>
      {k.vouchers.length ? k.vouchers.map((v) => {
        const verlopen = !!v.verloopt && new Date(v.verloopt).getTime() < nu
        return (
          <div key={v.id} className="beloning-rij">
            <span className="vul"><b>{v.naam}</b><br /><span className="stil klein">
              uitgegeven {datum(v.uitgegeven)}{v.verloopt ? ` · geldig t/m ${datum(v.verloopt)}` : ''}</span></span>
            {v.gebruikt ? <span className="badge">Gebruikt {datum(v.gebruikt)}</span>
              : verlopen ? <span className="badge dicht">Verlopen</span>
                : <button className="knop klein-knop gevaar" onClick={async () => {
                  if (!confirm(`Voucher "${v.naam}" intrekken?`)) return
                  try { await vraag('voucher-intrekken', { id: v.id }); await herlaad() } catch (e) { zetFout(foutTekst(e)) }
                }}><Ban size={16} />Intrekken</button>}
          </div>
        )
      }) : <p className="stil">Geen vouchers.</p>}
      <div className="filter-balk" style={{ marginTop: 10 }}>
        <select className="invoer" value={kies} onFocus={async () => {
          if (!promoties) zetPromoties((await vraag<{ promoties: { id: string; naam: string; actief: boolean }[] }>('promoties')).promoties.filter((p) => p.actief))
        }} onChange={(e) => zetKies(e.target.value)}>
          <option value="">Voucher geven…</option>
          {(promoties ?? []).map((p) => <option key={p.id} value={p.id}>{p.naam}</option>)}
        </select>
        <button className="knop" disabled={!kies} onClick={async () => {
          zetFout(null)
          try { await vraag('voucher-geven', { promotie: kies, klant: k.id }); zetKies(''); await herlaad() } catch (e) { zetFout(foutTekst(e)) }
        }}><Plus size={18} />Geven</button>
      </div>
      {fout && <div className="melding fout">{fout}</div>}
    </section>
  )
}
